import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  IconButton,
  Alert,
  Chip,
  Button,
  Skeleton,
} from '@mui/material';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import PictureAsPdf from '@mui/icons-material/PictureAsPdf';
import ArrowBack from '@mui/icons-material/ArrowBack';
import {
  getProjectAuditReport,
  type ProjectAuditReportResponse,
  type ProjectAuditReportView,
  type ReportPhase,
  type ReportAction,
  type ReportRejection,
} from '../services/projectApi';
import { dateStringToLocalDate } from '../utils/dateUtils';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const FRAPORT_BLUE = '#003366';
const FRAMEWORK_ACCENT = '#E8B923';

function formatDate(value: string | null): string {
  if (!value) return '—';
  const local = dateStringToLocalDate(value);
  if (local) {
    return local.toLocaleDateString(undefined, { dateStyle: 'short' });
  }
  try {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString(undefined, { dateStyle: 'short' });
  } catch {
    return value;
  }
}

function formatDateTime(value: string | null): string {
  if (!value) return '—';
  try {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? value : d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return value;
  }
}

export function ProjectAuditReportPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const projectIdParam = searchParams.get('projectId');
  const projectId = projectIdParam ? parseInt(projectIdParam, 10) : null;

  const [view, setView] = useState<ProjectAuditReportView>('official');
  const [report, setReport] = useState<ProjectAuditReportResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exportingPdf, setExportingPdf] = useState(false);

  useEffect(() => {
    if (!projectId || isNaN(projectId)) {
      setError('Missing or invalid projectId in URL. Use ?projectId=123');
      setReport(null);
      return;
    }
    setError(null);
    setLoading(true);
    getProjectAuditReport(projectId, view)
      .then(setReport)
      .catch((err: { message?: string }) => {
        setError(err.message || 'Failed to load project report');
        setReport(null);
      })
      .finally(() => setLoading(false));
  }, [projectId, view]);

  const handleViewChange = (_e: React.MouseEvent<HTMLElement>, newView: ProjectAuditReportView | null) => {
    if (newView) setView(newView);
  };

  const handleExportPdf = useCallback(() => {
    if (!report) return;
    setExportingPdf(true);
    try {
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      let y = 14;
      doc.setDrawColor(0, 51, 102);
      doc.setFillColor(0, 51, 102);
      doc.rect(0, 0, 210, 28, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(14);
      doc.text('FRAmework', 14, 12);
      doc.setFontSize(10);
      doc.text(
        report.view === 'complete' ? 'Complete Project Report' : 'Official Project Report',
        14,
        20
      );
      doc.text(
        `Generated: ${formatDateTime(report.generatedAt)}`,
        14,
        26
      );
      doc.setTextColor(0, 0, 0);
      y = 32;
      doc.setFontSize(12);
      doc.text(report.projectName, 14, y);
      y += 6;
      doc.setFontSize(9);
      doc.text(`Status: ${report.status}  |  Start: ${formatDate(report.projectStartDate)}`, 14, y);
      y += 5;
      if (report.projectLockedUtc) {
        doc.text(`Project locked: ${formatDateTime(report.projectLockedUtc)}`, 14, y);
        y += 5;
      }
      y += 3;
      const summary = report.summary;
      doc.text(
        `Phases: ${summary.totalPhases}  |  Tasks: ${summary.totalTasks}  |  Actions: ${summary.totalActions} (${summary.completedActions} completed)`,
        14,
        y
      );
      y += 6;
      if (report.view === 'complete' && (summary.totalChangeOrders > 0 || summary.totalRejections > 0)) {
        doc.text(
          `Change orders: ${summary.totalChangeOrders}  |  Rejections: ${summary.totalRejections}  |  Schedule delta: ${summary.scheduleDeltaDays ?? 0} days`,
          14,
          y
        );
        y += 6;
      }
      y += 4;

      report.phases.forEach((phase) => {
        if (y > 270) {
          doc.addPage();
          y = 14;
        }
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text(phase.name, 14, y);
        doc.setFont('helvetica', 'normal');
        y += 5;
        phase.tasks.forEach((task) => {
          if (y > 275) {
            doc.addPage();
            y = 14;
          }
          doc.setFontSize(9);
          doc.text(`  ${task.name}`, 18, y);
          y += 4;
          task.actions.forEach((action) => {
            if (y > 278) {
              doc.addPage();
              y = 14;
            }
            const name = action.name || action.actionType;
            const dates = `${formatDate(action.contractedStartDate)} – ${formatDate(action.contractedEndDate)} | Actual: ${formatDate(action.actualStartDate)} – ${formatDate(action.actualEndDate)}`;
            const completed = action.completedBy ? action.completedBy.displayName : '—';
            const completedAt = action.completedBy?.completedAt ? formatDateTime(action.completedBy.completedAt) : '—';
            doc.setFontSize(8);
            doc.text(`    ${name} | ${action.status} | ${dates} | By: ${completed} | Completed at: ${completedAt}`, 22, y);
            y += 4;
            // Comments & history for this action (Complete view)
            const rejectionsForAction = report.rejections?.filter((r) => r.entityType === 'ACTION' && r.entityId === action.actionId) ?? [];
            const hasComments =
              action.documentComments.some((dc) => dc.versionedComments.length > 0) ||
              action.completionComment ||
              (report.view === 'complete' && action.approvalHistory.length > 0) ||
              rejectionsForAction.length > 0;
            if (hasComments) {
              doc.setFontSize(7);
              if (rejectionsForAction.length > 0) {
                rejectionsForAction.forEach((r) => {
                  const note = r.notes && typeof r.notes === 'object' && 'Comment' in (r.notes as object)
                    ? String((r.notes as { Comment?: string }).Comment ?? '')
                    : (r.notes != null ? String(r.notes).slice(0, 60) : '—');
                  doc.text(`      Rejection ${formatDateTime(r.timestampUtc)} — ${r.actorDisplayName || r.actorEmail || '—'}: ${note.slice(0, 70)}`, 24, y);
                  y += 3;
                });
              }
              if (action.completionComment) {
                doc.text(`      ${action.completionComment.slice(0, 90)}`, 24, y);
                y += 3;
              }
              if (report.view === 'complete' && action.approvalHistory.length > 0) {
                action.approvalHistory.forEach((h) => {
                  doc.text(`      ${h.result}: ${h.actorEmail} ${h.occurredUtc ? formatDateTime(h.occurredUtc) : ''}${h.comment ? ` — ${h.comment.slice(0, 50)}` : ''}`, 24, y);
                  y += 3;
                });
              }
              action.documentComments.forEach((dc) => {
                dc.versionedComments.forEach((c) => {
                  doc.text(`      [${c.source}] ${c.commentText.slice(0, 70)}`, 24, y);
                  y += 3;
                });
              });
              y += 2;
            }
          });
        });
        y += 4;
      });

      // Project timeline (Complete view)
      if (report.view === 'complete' && report.timelineEvents && report.timelineEvents.length > 0) {
        if (y > 230) {
          doc.addPage();
          y = 14;
        }
        y += 4;
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('Project timeline (all transactions)', 14, y);
        doc.setFont('helvetica', 'normal');
        y += 6;
        const timelineHeaders = ['Time', 'Event', 'Entity / Action', 'Actor', 'Details'];
        const timelineRows = report.timelineEvents.map((ev) => {
          const eventLabel =
            ev.type === 'project_locked' ? 'Project locked'
            : ev.type === 'project_completed' ? 'Project completed'
            : ev.type === 'rejection' ? 'Rejection'
            : ev.type === 'change_order_committed' ? 'Change order committed'
            : ev.type === 'change_order_rejected' ? 'Change order rejected'
            : ev.type === 'approval' ? 'Approval'
            : ev.type === 'action_completed' ? 'Action completed'
            : ev.type;
          const entity =
            ev.type === 'project_locked' || ev.type === 'project_completed' ? 'Project'
            : ev.phaseName != null ? `${ev.phaseName} → ${ev.taskName} → ${ev.actionName ?? ev.actionType}`
            : ev.entityType != null && ev.entityId != null ? `${ev.entityType} #${ev.entityId}`
            : ev.changeOrderNumber != null ? `${ev.changeOrderNumber}${ev.title ? `: ${ev.title}` : ''}`
            : '—';
          const actor = ev.actorDisplayName || ev.actorEmail || '—';
          const rawDetails = ev.details ?? ev.comment ?? '—';
          const details = (rawDetails === 'undefined' || rawDetails === '') ? '—' : rawDetails;
          return [formatDateTime(ev.timestampUtc), eventLabel, entity.slice(0, 35), actor.slice(0, 25), String(details).slice(0, 40)];
        });
        autoTable(doc, {
          head: [timelineHeaders],
          body: timelineRows,
          startY: y,
          styles: { fontSize: 6 },
          headStyles: { fontSize: 6 },
          margin: { left: 14, right: 14 },
        });
        const docExt = doc as unknown as { lastAutoTable?: { finalY: number } };
        y = (docExt.lastAutoTable?.finalY ?? y) + 6;
      }

      if (report.view === 'complete' && report.changeOrders && report.changeOrders.length > 0) {
        if (y > 250) {
          doc.addPage();
          y = 14;
        }
        y += 4;
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('Change Orders', 14, y);
        doc.setFont('helvetica', 'normal');
        y += 6;
        const coHeaders = ['Number', 'Title', 'Status', 'Requested', 'Submitted', 'Approved', 'Schedule Δ'];
        const coRows = report.changeOrders.map((co) => [
          co.changeOrderNumber,
          (co.title || '').slice(0, 25),
          co.status,
          formatDate(co.requestedDate),
          co.submittedOnUtc ? formatDateTime(co.submittedOnUtc) : '—',
          co.approvedDate ? formatDate(co.approvedDate) : '—',
          co.scheduleDeltaDays != null ? String(co.scheduleDeltaDays) : '—',
        ]);
        autoTable(doc, {
          head: [coHeaders],
          body: coRows,
          startY: y,
          styles: { fontSize: 7 },
          headStyles: { fontSize: 7 },
          margin: { left: 14, right: 14 },
        });
        const docExt = doc as unknown as { lastAutoTable?: { finalY: number } };
        y = (docExt.lastAutoTable?.finalY ?? y) + 6;
      }

      if (report.view === 'complete' && report.rejections && report.rejections.length > 0) {
        if (y > 240) {
          doc.addPage();
          y = 14;
        }
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('Rejections', 14, y);
        doc.setFont('helvetica', 'normal');
        y += 6;
        const rejHeaders = ['Time', 'Entity', 'Actor', 'Details'];
        const rejRows = report.rejections.map((r) => {
          const note = r.notes && typeof r.notes === 'object' && 'Comment' in (r.notes as object)
            ? String((r.notes as { Comment?: string }).Comment ?? '')
            : (r.notes != null ? JSON.stringify(r.notes).slice(0, 40) : '—');
          return [
            formatDateTime(r.timestampUtc),
            r.entityId != null ? `${r.entityType} #${r.entityId}` : r.entityType,
            r.actorDisplayName || r.actorEmail || '—',
            note,
          ];
        });
        autoTable(doc, {
          head: [rejHeaders],
          body: rejRows,
          startY: y,
          styles: { fontSize: 7 },
          headStyles: { fontSize: 7 },
          margin: { left: 14, right: 14 },
        });
        const docExt2 = doc as unknown as { lastAutoTable?: { finalY: number } };
        y = (docExt2.lastAutoTable?.finalY ?? y) + 6;
      }

      // Document versions (Complete view)
      if (report.view === 'complete' && report.documentVersions && report.documentVersions.length > 0) {
        if (y > 240) {
          doc.addPage();
          y = 14;
        }
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('Document versions', 14, y);
        doc.setFont('helvetica', 'normal');
        y += 6;
        report.documentVersions.forEach((d) => {
          if (y > 275) {
            doc.addPage();
            y = 14;
          }
          doc.setFontSize(9);
          doc.text(d.name, 14, y);
          y += 4;
          doc.setFontSize(7);
          if (d.path || d.lastModifiedUtc) {
            doc.text(`${d.path || '—'} · Last modified: ${d.lastModifiedUtc ? formatDateTime(d.lastModifiedUtc) : '—'}`, 18, y);
            y += 4;
          }
          if (d.comments && d.comments.length > 0) {
            d.comments.forEach((c) => {
              doc.text(`[${c.source}] ${c.commentText.slice(0, 80)}${c.commentedAt ? ` (${formatDateTime(c.commentedAt)})` : ''}`, 18, y);
              y += 3;
            });
          }
          y += 2;
        });
      }

      doc.save(`project-report-${report.view}-${report.projectId}-${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (err) {
      console.error('PDF export failed:', err);
    } finally {
      setExportingPdf(false);
    }
  }, [report]);

  if (!projectId || isNaN(projectId)) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Typography variant="h5" sx={{ color: 'white', mb: 2 }}>
          Project Report
        </Typography>
        <Alert severity="warning">
          No project selected. Open this page from Project Execution using the Project Report button in the toolbar, or add ?projectId= to the URL.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }} className="project-audit-report-print">
      {/* Branded header - visible on screen and in print */}
      <Paper
        sx={{
          p: 2,
          mb: 3,
          backgroundColor: FRAPORT_BLUE,
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
        }}
        className="project-report-header"
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {projectId && !isNaN(projectId) && (
            <Button
              startIcon={<ArrowBack />}
              onClick={() => navigate(`/execution?projectId=${projectId}`)}
              sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.7)', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}
              variant="outlined"
              size="small"
              className="no-print"
            >
              Back to execution
            </Button>
          )}
          <Typography variant="h6">
            {view === 'complete' ? 'Complete Project Report' : 'Official Project Report'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }} className="no-print">
          <ToggleButtonGroup
            value={view}
            exclusive
            onChange={handleViewChange}
            size="small"
            sx={{ bgcolor: 'rgba(255,255,255,0.2)', '& .MuiToggleButton-root': { color: 'white' } }}
          >
            <ToggleButton value="official" aria-label="Official view">Official</ToggleButton>
            <ToggleButton value="complete" aria-label="Complete view">Complete</ToggleButton>
          </ToggleButtonGroup>
          <Button
            variant="contained"
            size="small"
            startIcon={<PictureAsPdf />}
            onClick={handleExportPdf}
            disabled={!report || exportingPdf}
            sx={{ bgcolor: FRAMEWORK_ACCENT, color: FRAPORT_BLUE, '&:hover': { bgcolor: '#d4a01e' } }}
          >
            {exportingPdf ? 'Exporting…' : 'Download PDF'}
          </Button>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && (
        <Box sx={{ py: 4 }}>
          <Skeleton variant="rectangular" height={120} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" height={200} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" height={160} />
        </Box>
      )}

      {!loading && report && (
        <>
          {/* Project title and generated timestamp */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  {report.projectName}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Chip
                    label={report.status}
                    size="small"
                    color={report.status === 'COMPLETE' ? 'success' : 'default'}
                  />
                  <Typography variant="body2" color="text.secondary">
                    Start: {formatDate(report.projectStartDate)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Report generated: {formatDateTime(report.generatedAt)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Paper>

          {/* Executive summary */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Executive Summary
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">Phases</Typography>
                <Typography variant="h6">{report.summary.totalPhases}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Tasks</Typography>
                <Typography variant="h6">{report.summary.totalTasks}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Actions</Typography>
                <Typography variant="h6">
                  {report.summary.completedActions} / {report.summary.totalActions} completed
                </Typography>
              </Box>
              {report.summary.overdueActions > 0 && (
                <Box>
                  <Typography variant="body2" color="error">Overdue actions</Typography>
                  <Typography variant="h6" color="error">{report.summary.overdueActions}</Typography>
                </Box>
              )}
              {report.view === 'complete' && (
                <>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Change orders</Typography>
                    <Typography variant="h6">{report.summary.totalChangeOrders}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Rejections</Typography>
                    <Typography variant="h6">{report.summary.totalRejections}</Typography>
                  </Box>
                  {report.summary.scheduleDeltaDays != null && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">Net schedule impact (days)</Typography>
                      <Typography variant="h6">{report.summary.scheduleDeltaDays}</Typography>
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Paper>

          {/* Phase / Task / Action timeline */}
          {report.phases.map((phase) => (
            <PhaseSection key={phase.phaseId} phase={phase} isCompleteView={report.view === 'complete'} />
          ))}

          {/* Project timeline / audit log (Complete view): rejections, change orders, approvals, completions in chronological order */}
          {report.view === 'complete' && report.timelineEvents && report.timelineEvents.length > 0 && (
            <Paper sx={{ p: 2, mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Project timeline (all transactions)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Chronological audit of rejections, change orders, approvals, and action completions with exact time.
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Time</TableCell>
                      <TableCell>Event</TableCell>
                      <TableCell>Plan ID</TableCell>
                      <TableCell>Entity / Action</TableCell>
                      <TableCell>Source Doc</TableCell>
                      <TableCell>Reject Action</TableCell>
                      <TableCell>Actor</TableCell>
                      <TableCell>Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {report.timelineEvents.map((ev, idx) => (
                      <TableRow key={idx}>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(ev.timestampUtc)}</TableCell>
                        <TableCell>
                          {ev.type === 'project_locked' && 'Project locked'}
                          {ev.type === 'project_completed' && 'Project completed'}
                          {ev.type === 'rejection' && 'Rejection'}
                          {ev.type === 'change_order_committed' && 'Change order committed'}
                          {ev.type === 'change_order_rejected' && 'Change order rejected'}
                          {ev.type === 'approval' && 'Approval'}
                          {ev.type === 'action_completed' && 'Action completed'}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{ev.planId ?? '—'}</TableCell>
                        <TableCell>
                          {(ev.type === 'project_locked' || ev.type === 'project_completed') && 'Project'}
                          {ev.phaseName != null && `${ev.phaseName} → ${ev.taskName} → ${ev.actionName ?? ev.actionType}`}
                          {ev.entityType != null && ev.entityId != null && `${ev.entityType} #${ev.entityId}`}
                          {ev.changeOrderNumber != null && `${ev.changeOrderNumber}${ev.title ? `: ${ev.title}` : ''} (v${ev.fromVersion}→v${ev.toVersion})`}
                          {ev.type === 'rejection' && ev.entityType == null && '—'}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{ev.sourceDocPlanId ?? '—'}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{ev.rejectActionPlanId ?? '—'}</TableCell>
                        <TableCell>
                          {ev.actorDisplayName ?? ev.actorEmail ?? ev.completedByDisplayName ?? ev.completedByEmail ?? ev.requestedBy ?? ev.approvedBy ?? '—'}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 320 }}>
                          {ev.type === 'approval' && (ev.details == null || ev.details === '' || ev.details === 'undefined') && (ev.comment == null || ev.comment === '' || ev.comment === 'undefined')
                            ? 'no comment entered'
                            : (() => {
                                const r = ev.details ?? ev.comment ?? (ev.scheduleDeltaDays != null ? `Schedule impact: ${ev.scheduleDeltaDays} days` : null) ?? (ev.status ? `Status: ${ev.status}` : null) ?? '—';
                                return (r === 'undefined' || r === '') ? '—' : r;
                              })()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {report.view === 'complete' && report.changeOrders && report.changeOrders.length > 0 && (
            <Paper sx={{ p: 2, mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Change Orders
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Number</TableCell>
                      <TableCell>Title</TableCell>
                      <TableCell>Summary</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Requested by</TableCell>
                      <TableCell>Requested date</TableCell>
                      <TableCell>Submitted at</TableCell>
                      <TableCell>Approved by</TableCell>
                      <TableCell>Approved date</TableCell>
                      <TableCell>Version</TableCell>
                      <TableCell>Schedule impact</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {report.changeOrders.map((co) => (
                      <TableRow key={co.changeOrderId}>
                        <TableCell>{co.changeOrderNumber}</TableCell>
                        <TableCell>{co.title || '—'}</TableCell>
                        <TableCell sx={{ maxWidth: 200 }}>{co.summary ? String(co.summary).slice(0, 80) + (String(co.summary).length > 80 ? '…' : '') : '—'}</TableCell>
                        <TableCell><Chip label={co.status} size="small" /></TableCell>
                        <TableCell>{co.requestedBy || '—'}</TableCell>
                        <TableCell>{formatDate(co.requestedDate)}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{co.submittedOnUtc ? formatDateTime(co.submittedOnUtc) : '—'}</TableCell>
                        <TableCell>{co.approvedBy || '—'}</TableCell>
                        <TableCell>{co.approvedDate ? formatDate(co.approvedDate) : '—'}</TableCell>
                        <TableCell>v{co.fromVersion} → v{co.toVersion}</TableCell>
                        <TableCell>{co.scheduleDeltaDays != null ? `${co.scheduleDeltaDays} days` : '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {report.view === 'complete' && report.rejections && report.rejections.length > 0 && (
            <Paper sx={{ p: 2, mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Rejections (chronological)
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Time (UTC)</TableCell>
                      <TableCell>Entity</TableCell>
                      <TableCell>Actor</TableCell>
                      <TableCell>Project date</TableCell>
                      <TableCell>Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {report.rejections.map((r) => (
                      <TableRow key={r.logId}>
                        <TableCell>{formatDateTime(r.timestampUtc)}</TableCell>
                        <TableCell>
                          {r.entityId != null ? `${r.entityType} #${r.entityId}` : r.entityType}
                        </TableCell>
                        <TableCell>{r.actorDisplayName || r.actorEmail || '—'}</TableCell>
                        <TableCell>{r.projectDate}</TableCell>
                        <TableCell>
                          {r.notes && typeof r.notes === 'object' && 'Comment' in (r.notes as object)
                            ? String((r.notes as { Comment?: string }).Comment ?? '')
                            : r.notes != null ? JSON.stringify(r.notes) : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {report.view === 'complete' && report.documentVersions && report.documentVersions.length > 0 && (
            <Paper sx={{ p: 2, mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Document versions
              </Typography>
              {report.documentVersions.map((d) => (
                <Box key={d.spItemId} sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">{d.name}</Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {d.path || '—'} · Last modified: {d.lastModifiedUtc ? formatDateTime(d.lastModifiedUtc) : '—'}
                  </Typography>
                  {d.comments && d.comments.length > 0 && (
                    <Box sx={{ pl: 2, mt: 0.5 }}>
                      {d.comments.map((c, i) => (
                        <Typography key={i} variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          [{c.source}] {c.commentText}
                          {c.commentedAt && ` (${formatDateTime(c.commentedAt)})`}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Box>
              ))}
            </Paper>
          )}
        </>
      )}
    </Container>
  );
}

function PhaseSection({
  phase,
  isCompleteView,
  rejections,
}: {
  phase: ReportPhase;
  isCompleteView: boolean;
  rejections?: ReportRejection[];
}) {
  const [open, setOpen] = useState(true);
  return (
    <Paper sx={{ mb: 2, overflow: 'hidden' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: 2,
          bgcolor: 'grey.100',
          cursor: 'pointer',
        }}
        onClick={() => setOpen(!open)}
      >
        <IconButton size="small" aria-label={open ? 'collapse' : 'expand'} className="no-print">
          {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
        </IconButton>
        <Typography variant="h6" sx={{ flex: 1 }}>
          {phase.name}
        </Typography>
        <Chip label={phase.status} size="small" sx={{ mr: 1 }} />
        <Typography variant="body2" color="text.secondary">
          Contracted: {formatDate(phase.contractedStartDate)} – {formatDate(phase.contractedEndDate)}
          {' · Actual: '}
          {formatDate(phase.actualStartDate)} – {formatDate(phase.actualEndDate)}
        </Typography>
      </Box>
      <Collapse in={open}>
        <Box sx={{ px: 2, pb: 2 }}>
          {phase.tasks.map((task) => (
            <Box key={task.taskId} sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                {task.name}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Contracted: {formatDate(task.contractedStartDate)} – {formatDate(task.contractedEndDate)}
                {' · Actual: '}
                {formatDate(task.actualStartDate)} – {formatDate(task.actualEndDate)}
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Action</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Contracted dates</TableCell>
                      <TableCell>Actual dates</TableCell>
                      <TableCell>Assigned / Completed by</TableCell>
                      <TableCell>Completed at</TableCell>
                      <TableCell>Source documents</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {task.actions.map((action) => (
                      <ActionRow key={action.actionId} action={action} />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {task.actions.some(
                (a) =>
                  a.documentComments.some((dc) => dc.versionedComments.length > 0) ||
                  a.completionComment ||
                  (isCompleteView && a.approvalHistory.length > 0) ||
                  (rejections && rejections.some((r) => r.entityType === 'ACTION' && r.entityId === a.actionId))
              ) && (
                <Box sx={{ pl: 2 }}>
                  {task.actions.map((action) => (
                    <ActionComments
                      key={action.actionId}
                      action={action}
                      isCompleteView={isCompleteView}
                      rejectionsForAction={
                        rejections?.filter((r) => r.entityType === 'ACTION' && r.entityId === action.actionId) ?? []
                      }
                    />
                  ))}
                </Box>
              )}
            </Box>
          ))}
        </Box>
      </Collapse>
    </Paper>
  );
}

function ActionRow({ action }: { action: ReportAction }) {
  const assigned = action.assignedTo.length > 0 ? action.assignedTo.map((u) => u.displayName).join(', ') : '—';
  const completed = action.completedBy ? action.completedBy.displayName : '—';
  const completedAt = action.completedBy?.completedAt ? formatDateTime(action.completedBy.completedAt) : '—';
  return (
    <TableRow>
      <TableCell>{action.name || action.actionType}</TableCell>
      <TableCell>{action.actionType}</TableCell>
      <TableCell>
        <Chip label={action.status} size="small" color={action.status === 'COMPLETE' ? 'success' : 'default'} />
      </TableCell>
      <TableCell>
        {formatDate(action.contractedStartDate)} – {formatDate(action.contractedEndDate)}
      </TableCell>
      <TableCell>
        {formatDate(action.actualStartDate)} – {formatDate(action.actualEndDate)}
      </TableCell>
      <TableCell>
        Assigned: {assigned} {action.completedBy && ` · Completed by: ${completed}`}
      </TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>{completedAt}</TableCell>
      <TableCell>
        {action.sourceDocuments.length > 0
          ? action.sourceDocuments.map((d) => d.documentTitle || d.documentName || `Document ${d.spItemId}`).join(', ')
          : '—'}
      </TableCell>
    </TableRow>
  );
}

function ActionComments({
  action,
  isCompleteView,
  rejectionsForAction = [],
}: {
  action: ReportAction;
  isCompleteView: boolean;
  rejectionsForAction?: ReportRejection[];
}) {
  const hasComments =
    action.documentComments.some((dc) => dc.versionedComments.length > 0) ||
    action.completionComment ||
    (isCompleteView && action.approvalHistory.length > 0) ||
    rejectionsForAction.length > 0;
  if (!hasComments) return null;
  const rejectionNote = (notes: unknown): string => {
    if (notes && typeof notes === 'object' && 'Comment' in (notes as object))
      return String((notes as { Comment?: string }).Comment ?? '');
    return notes != null ? String(notes) : '';
  };
  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="caption" color="text.secondary">
        {action.name || action.actionType} – comments & history
      </Typography>
      {rejectionsForAction.length > 0 && (
        <Box sx={{ mt: 0.5 }}>
          {rejectionsForAction.map((r, i) => (
            <Typography key={`rej-${i}`} variant="body2" color="error.main">
              Rejection {formatDateTime(r.timestampUtc)} — {r.actorDisplayName || r.actorEmail || '—'}: {rejectionNote(r.notes)}
            </Typography>
          ))}
        </Box>
      )}
      {action.completionComment && (
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {action.completionComment}
        </Typography>
      )}
      {isCompleteView && action.approvalHistory.length > 0 && (
        <Box sx={{ mt: 0.5 }}>
          {action.approvalHistory.map((h, i) => (
            <Typography key={i} variant="body2" color="text.secondary">
              {h.result}: {h.actorEmail} {h.occurredUtc ? formatDateTime(h.occurredUtc) : ''}
              {h.comment && ` — ${h.comment}`}
            </Typography>
          ))}
        </Box>
      )}
      {action.documentComments.map((dc) =>
        dc.versionedComments.length > 0 ? (
          <Typography key={dc.spItemId} variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {dc.versionedComments.map((c) => `[${c.source}] ${c.commentText}`).join('\n')}
          </Typography>
        ) : null
      )}
    </Box>
  );
}

export default ProjectAuditReportPage;
